import serviceToken from './requestToken';
import { BaseResponse } from './types';

const RETAILER_BASE_API = process.env.REACT_APP_RETAILER_BASE_API;
const API_URL_DSZ = process.env.REACT_APP_API_URL_DSZ;

export type RetailerInfo = {
  // base info
  id: number;
  email: string;
  created_at: string;
  updated_at: string;
  group_id: number;
  lastname: string;
  firstname: string;

  // business details
  rs_company: string;
  rs_abn: string;
  rs_telephone: string;
  rs_credit_limit: string;
  rs_account_status: number;
  ds_email_secondary: string;

  // account manager
  rs_account_manager: string;
  rs_account_manager_email: string;

  // api details
  rs_allow_api_pwd: string;
};

export const getRetailerInfoSvc = async (
  retailerId: string
): Promise<BaseResponse<RetailerInfo>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/${retailerId}`,
    {
      params: {}
    }
  );

  return res.data;
};

export type RetailerInfoUpdate = Partial<
  Pick<RetailerInfo, 'ds_email_secondary'>
>;

export const updateRetailerInfoSvc = async (
  retailerId: string,
  data: RetailerInfoUpdate
): Promise<BaseResponse<RetailerInfo>> => {
  const res = await serviceToken.put(
    RETAILER_BASE_API + `retailers/${retailerId}`,
    data
  );
  return res.data;
};

export type RetailerBillingInfo = {
  customer_id: number;
  email: string;
  telephone: string;
  country: CountryCode;
  postcode: string;
  state: string;
  suburb: string;
  street: string;
  company: string;
  lastname: string;
  firstname: string;
  address_type: 'billing' | 'shipping';
};

export type RetailerBillingAddressUpdate = Partial<
  Pick<
    RetailerBillingInfo,
    | 'country'
    | 'postcode'
    | 'state'
    | 'suburb'
    | 'street'
    | 'telephone'
    | 'lastname'
    | 'firstname'
  >
>;

export const getRetailerBillingAddressSvc = async (
  retailerId: string
): Promise<BaseResponse<RetailerBillingInfo>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/${retailerId}/address/billing`,
    {
      params: {}
    }
  );

  return res.data;
};

export const updateRetailerBillingAddressSvc = async (
  retailerId: string,
  data: RetailerBillingAddressUpdate
): Promise<BaseResponse<RetailerBillingInfo>> => {
  const res = await serviceToken.put(
    RETAILER_BASE_API + `retailers/${retailerId}/address/billing`,
    data
  );
  return res.data;
};

export type CountryCode = 'AU' | 'NZ';

export type CountryRegionInfo = {
  region_id: number;
  country_id: CountryCode;
  default_name: string;
  code: string;
};

export const getCountryRegionSvc = async (
  country: CountryCode
): Promise<BaseResponse<CountryRegionInfo[]>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/country-region`,
    {
      params: { country_id: country }
    }
  );
  return res.data;
};

export type PostcodeInfo = {
  suburb: string;
  postcode: string;
  state: string;
  zone_rate_code: string;
};

export const getPostcodeSvc = async (
  country: CountryCode,
  params: {
    postcode?: string;
    suburb?: string;
  }
): Promise<PostcodeInfo[]> => {
  const res = await serviceToken.get(API_URL_DSZ + `/postcodes/${country}`, {
    params
  });
  return res.data.result;
};

export type VerificationCodeResponse = {};

export type VerificationCodeVerify = {
  code: string;
};

export type VerificationCodeVerifyResponse = {};

export enum VerificationCodeType {
  PASSWORD = 'RP',
  API_PASSWORD = 'RAP'
}

export const sendVerificationCodeSvc = async (
  retailerId: string,
  type: VerificationCodeType
): Promise<BaseResponse<VerificationCodeResponse>> => {
  const res = await serviceToken.post(
    RETAILER_BASE_API + `verification/send-code`,
    { customerId: retailerId, type: type }
  );
  return res.data;
};

export const verifyCodeSvc = async (
  retailerId: string,
  data: VerificationCodeVerify
): Promise<BaseResponse<VerificationCodeVerifyResponse>> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // we only check the code is not empty, we will check the code in the password change api
      if (data.code && data.code.trim() !== '') {
        resolve({
          success: true,
          message: 'Please change your password',
          data: {}
        });
      } else {
        reject(new Error('Invalid verification code. Please try again.'));
      }
    }, 800);
  });
};

// Password change types and service
export type PasswordChangeData = {
  oldPassword: string;
  newPassword: string;
  verificationCode: string;
};

export type PasswordChangeResponse = {};

export const changePasswordSvc = async (
  retailerId: string,
  data: PasswordChangeData
): Promise<BaseResponse<PasswordChangeResponse>> => {
  const res = await serviceToken.post(
    RETAILER_BASE_API + `retailers/${retailerId}/reset-password`,
    data
  );
  return res.data;
};
